import React, { useContext, useState, useEffect, useMemo } from 'react'
/** @jsx jsx */
import { jsx } from '@emotion/core'
import qs from 'querystring'
import { connect } from 'react-redux'
import Grid, {
  virtualizedGridRenderer,
  defaultVirtualizedCellRender,
  VirtualizedCell,
  extractAndFormatData,
} from '@integec/grid-tools'
import { strCol, numCol, dateCol, intCol } from '@integec/grid-tools/lib/cols'
import R from 'ramda'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import { Button, DatePicker, Upload, Input, Icon, message, Select } from 'antd'
import {
  getWindUtilitySelection,
  getWindPowerData,
  getWindComponent,
  postWindPowerData,
  getWindUnitStatusType,
  windLevel2validation,
  getAuthorization,
  getCredentials,
} from 'api/pgads'
import { DatePickerContext, ErrorContext, SpinContext } from 'context'
import {
  getEnvConfig,
  getPermitActions,
  getApiTargetInfo,
  getValidYearRange,
} from 'core'
import { modifiedDateCol, modifierCol } from 'core/HeaderColTypeProps'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
} from 'core/screen-util'

import { rowHeaderRender, hasAccesPermits } from 'core/utils'
import * as ScreenPermissions from 'core/EventActions'
import JcTmpErrorDialog, {
  isErrorContains,
} from 'core/component/JcTmpErrorDialog'
import * as freeEditEngine from '@integec/grid-tools/lib/editEngine'
import NercexportDialog from './NercExportDialog'

const WindPowerData = ({
  apiTargetInfo,
  envConfig,
  actionPermits,
  validYearRange,
}) => {
  const { selectedDate, setSelectedDate } = useContext(DatePickerContext)
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)

  const [data, setData] = useState([])
  const [outageAllData, setOutageAllData] = useState([])
  const [outageData, setOutageData] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState('')
  const [utilitySelectionList, setUtilitySelectList] = useState([])
  const [subgroupIdNameMap, setSubgroupIdNameMap] = useState({})
  const [utilityList, setUtilityList] = useState([])
  const [componentMap, setComponentListMap] = useState({})
  const [componentSystemMap, setCompoenentSystemMap] = useState({})
  const [componentIdNameMap, setComponentIdNameMap] = useState({})
  const [selUtility, setSelUtility] = useState('ALL')
  const [unitStatusType, setUnitStatusType] = useState({})
  const [selectedPerformance, setSelectedPerformance] = useState([])
  const [selectedOutageData, setSelectedOutageData] = useState([])
  const [selectedComponenetName, setSelectComponentName] = useState('')
  const [selectedComponenetType, setSelectedComponentType] = useState('')
  const [tmpError, setTmpError] = useState([])
  const [showNercExportDialog, setShowNercExportDialog] = useState(false)
  const [importFileName, setImportFileName] = useState('')
  const [outageEditInfo, setOutageEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const [subgroupEditInfo, setsubgroupEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )

  const hasWindPowerAdminPermisiion = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.WIND_ADMIN_POWER_DATA],
        matchAny: true,
      }),
    [actionPermits, envConfig.authGodMode]
  )

  useEffect(
    _ => {
      getWindUnitStatusType(apiTargetInfo).fork(setError, data => {
        const unitStatusType = R.reduce(
          (a, b) => ({ ...a, ...b }),
          {},
          data.map(({ windUnitStatusId, windUnitStatus }) => ({
            [windUnitStatusId]: windUnitStatus,
          }))
        )
        setUnitStatusType(unitStatusType)
      })

      getWindUtilitySelection(apiTargetInfo).fork(setError, data => {
        setUtilitySelectList(data)
        setSubgroupIdNameMap(
          R.reduce(
            (a, b) => ({ ...a, ...b }),
            {},
            data.map(d => ({ [d.subgroupId]: d.subgroupName }))
          )
        )
        let utilSelList = R.reduce(
          (a, b) => ({ ...a, ...b }),
          { ALL: 'ALL' },
          data.map(({ utility, utilityName }) => ({ [utility]: utilityName }))
        )
        utilSelList = R.sortBy(
          R.prop('value'),
          Object.keys(utilSelList).map(k => ({
            key: k,
            value: utilSelList[k],
          }))
        )
        setUtilityList(utilSelList)
      })

      getWindComponent(apiTargetInfo).fork(setError, data => {
        const wt = R.groupBy(d => d.windSystem)(data)
        setComponentListMap(wt)
        const idNameMap = R.reduce(
          (a, b) => ({ ...a, ...b }),
          {},
          data.map(d => ({ [d.windComponentId]: d.windComponent }))
        )
        setComponentIdNameMap(idNameMap)
        const componentSystemMap = R.reduce(
          (a, b) => ({ ...a, ...b }),
          {},
          data.map(d => ({ [d.windComponentId]: d.windSystem }))
        )
        setCompoenentSystemMap(componentSystemMap)
      })
    },
    [apiTargetInfo, setError]
  )

  const retrieve = _ => {
    setSpinning(true)
    getWindPowerData(apiTargetInfo)({
      date: selectedDate,
      selection: selUtility,
    }).fork(setError, data => {
      setSpinning(false)

      let perfData = data.performanceData

      perfData = data.subGroupData
        .map(d => ({
          year: selectedDate.format('YYYY'),
          month: selectedDate.format('MM'),
          groupIdName: d.groupIdName,
          plantIdName: d.plantIdName,
          utility: d.utility,
          subgroupId: d.subgroupId,
        }))
        .map(d => {
          const found = perfData.find(
            f =>
              R.toLower(f.groupIdName) === R.toLower(d.groupIdName) &&
              R.toLower(f.plantIdName) === R.toLower(d.plantIdName) &&
              f.utility === d.utility &&
              f.subgroupId === d.subgroupId
          )
          return found ? { ...found, isOld: true } : d
        })

      const modfperfData = perfData.map(d => ({
        ...d,
        subgroupName: subgroupIdNameMap[d.subgroupId],
      }))

      if (modfperfData.find(s => s.subgroupName === undefined)) {
        setData(perfData)
      } else {
        setData(
          R.sortBy(R.compose(R.toLower, R.prop('subgroupName')))(
            modfperfData
          ) || []
        )
      }

      const outage = (data.outageData || []).map(d => ({
        ...d,
        componentName: getComponentSystemName(d.componentId),
      }))
      setOutageAllData(outage || [])
      setOutageEditInfo(freeEditEngine.generateInitialEditInfo())
      setsubgroupEditInfo(freeEditEngine.generateInitialEditInfo())
    })
  }

  useEffect(
    _ => {
      if ((selectedPerformance || []).length <= 0) return
      const { utility, plantIdName, groupIdName, subgroupId } =
        selectedPerformance[0] || {}

      const filteredData = outageAllData.filter(
        f =>
          f.utility === utility &&
          f.plantIdName === plantIdName &&
          f.groupIdName === groupIdName &&
          f.subgroupId === subgroupId
      )

      setOutageData(filteredData)
    },
    [outageAllData, selectedPerformance]
  )

  const setDataEditInfo = (
    editOrigInfo,
    setEditInfo,
    origData,
    setTargetData
  ) => {
    const modData = origData.map(d => {
      if (editOrigInfo.updatedMap.get(d)) {
        return {
          ...editOrigInfo.updatedMap.get(d),
          // modificationInfo: { user: null, modDate: moment() },
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    let editData = editOrigInfo.updated
    editData = editData.map(d => ({
      ...d,
      // modificationInfo: { user: null, modDate: moment() },
      modificationInfo: undefined,
    }))
    setTargetData(modData)
    setEditInfo({ ...editOrigInfo, updated: editData })
    // this.setState({
    //   data: modData,
    //   editOrigInfo: { ...editOrigInfo, updated: editData },
    // })
  }

  const getComponentSystemName = componentId => {
    return (
      '[' +
      componentSystemMap[componentId] +
      '] ' +
      componentIdNameMap[componentId] +
      '-' +
      componentId
    )
  }

  const onAddOutage = _ => {
    if (selectedComponenetType === '') {
      message.warning('Please select valid System & component')
      return
    }
    if (selectedPerformance.length > 1) {
      message.warning(
        'Please select exactly one performance entry to add outage'
      )
      return
    }
    if (selectedPerformance.length === 0) {
      message.warning('Please, Select Performance data')
      return
    }
    const {
      utility,
      plantIdName,
      groupIdName,
      subgroupId,
    } = selectedPerformance[0]

    const outageModifiedData = freeEditEngine.applyEdits({
      // data: outageData.filter(d => d.updated || d.inserted),
      data: outageData,
      editInfo: outageEditInfo,
    })
    // eslint-disable-next-line eqeqeq
    if (outageModifiedData.find(d => d.componentId == selectedComponenetType)) {
      message.info('Data already Inserted,please change it for update')
      return
    }
    const newOutage = {
      utility,
      plantIdName,
      groupIdName,
      subgroupId,
      year: selectedDate.format('YYYY'),
      month: selectedDate.format('MM'),
      componentId: selectedComponenetType,
      componentName: getComponentSystemName(selectedComponenetType),
      inserted: true,
      // modificationInfo: { user: null, modDate: moment() },
    }
    const newInfo = {
      ...outageEditInfo,
      added: [...outageEditInfo.added, newOutage],
    }
    setOutageEditInfo(newInfo)
  }

  const onSave = _ => {
    // outageData,outageEditInfo
    // data,subgroupEditInfo

    // let saveOutageData = freeEditEngine.applyEdits({
    //   data: data.filter(d => d.updated),
    //   editInfo,
    // })

    const outageModifiedData = freeEditEngine.applyEdits({
      // data: outageAllData.filter(d => d.updated || d.inserted),
      data: outageAllData,
      editInfo: outageEditInfo,
    })

    const outageDeletedData = R.uniqBy(
      R.prop('componentId'),
      outageEditInfo.removed
    )

    const subgroupModifiedData = freeEditEngine.applyEdits({
      data: data.filter(d => d.updated),
      editInfo: subgroupEditInfo,
    })

    const saveOutage = [
      ...(outageModifiedData || []).map(d => ({
        ...d,
        action: 'N',
        validated: 'N',
      })),
      ...(outageDeletedData || []).map(d => ({
        ...d,
        action: 'D',
        validated: 'N',
      })),
    ]
    setSpinning(true)
    postWindPowerData(apiTargetInfo)({
      updatedOutage: saveOutage,
      // removedOutage: outageDeletedData,
      updatedPerformance: [
        ...(subgroupModifiedData || []).map(d => ({ ...d, action: 'N' })),
      ],
    }).fork(setError, data => {
      setSpinning(false)
      setTmpError(data)
      if (data.length === 0) {
        retrieve()
        setOutageEditInfo(freeEditEngine.generateInitialEditInfo())
        setsubgroupEditInfo(freeEditEngine.generateInitialEditInfo())
      }
    })
  }

  const onRemoveOutage = _ => {
    if (selectedOutageData.length === 0) {
      message.info('Please , Select outage data to delete')
      return
    }

    let added = outageEditInfo.added

    if (added.length > 0) {
      const removed = outageEditInfo.removed

      added = added.filter(
        f =>
          !removed.find(
            r =>
              r.componentId === f.componentId &&
              r.subgroupId === f.subgroupId &&
              r.groupIdName === f.groupIdName
          )
      )
    }

    const newInfo = {
      ...outageEditInfo,
      added,
      removed: [...(outageEditInfo.removed || []), ...selectedOutageData],
    }
    setOutageEditInfo(newInfo)
  }

  useEffect(
    _ => {
      setData([])
      setSelectedPerformance([])
      setSelectedOutageData([])
      setOutageData([])
      setOutageEditInfo(freeEditEngine.generateInitialEditInfo())
      setsubgroupEditInfo(freeEditEngine.generateInitialEditInfo())
    },
    [selectedDate, selUtility]
  )

  const onValidation = _ => {
    setSpinning(true)

    // utilityList.filter(f=>f.key===selUtility)

    windLevel2validation(apiTargetInfo)({
      data: utilitySelectionList.filter(f => f.utility === selUtility),
      year: selectedDate.format('YYYY'),
      month: selectedDate.format('MM'),
    }).fork(setError, data => {
      setSpinning(false)
      setTmpError(data)
      if (data.length === 0) {
        message.info('Validation run successfully.')
      }
    })
  }

  const url = ft => serType =>
    `${
      apiTargetInfo.baseUrl
    }/windpowerdata/import-file-${serType}?${qs.stringify({
      fileName: ft,
    })}`
  const fileProps = {
    name: 'file',
    method: 'POST',
    headers: getAuthorization(apiTargetInfo),
    ...getCredentials(apiTargetInfo),
  }

  const onFileUploadChange = info => {
    setSpinning(true)
    if (info.file.status === 'done') {
      setSpinning(false)
      const tmpError = info.file.response
      setTmpError(tmpError)
      if (!isErrorContains(tmpError)) {
        message.info('File upload successfully')
        retrieve()
      }
    } else if (info.file.status === 'error') {
      setError(info.file.response)
      setSpinning(false)
    }
  }

  // eslint-disable-next-line no-unused-vars
  const customizedCellRender = params => {
    const { gridToolProps, reactVirtualizedProps } = params

    const { getCellProps, headers, data } = gridToolProps

    const { columnIndex, rowIndex, style } = reactVirtualizedProps

    const header = headers[columnIndex]
    const rowData = data[rowIndex]
    const cellData = extractAndFormatData({
      rowData: rowData,
      header: header,
    })

    if (rowData.isOld && header.isKey) {
      const cellProps = getCellProps({
        rowIndex,
        columnIndex,
        header: headers[columnIndex],
        data,
        style,
      })
      if (cellProps.isEditing) {
        return defaultVirtualizedCellRender({ ...params })
      }
      const oldRowProps = {
        ...cellProps,
        style: {
          ...style,
          // backgroundColor: 'yellow',
          color: 'red',
        },
      }
      // {...R.omit(['data'], oldRowProps)}
      return (
        <VirtualizedCell {...R.omit(['data'], oldRowProps)}>
          {cellData}
        </VirtualizedCell>
      )
    }

    return defaultVirtualizedCellRender({ ...params })
  }
  const PerformanceHeaders = useMemo(
    _ => WindPerformanceHeaders(subgroupIdNameMap, unitStatusType),
    [subgroupIdNameMap, unitStatusType]
  )
  const OutageHeaders = useMemo(
    _ => WindOutageHeaders(subgroupIdNameMap, componentIdNameMap),
    [subgroupIdNameMap, componentIdNameMap]
  )

  return (
    <div
      css={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <NercexportDialog
        apiTargetInfo={apiTargetInfo}
        utilityList={utilityList}
        visible={showNercExportDialog}
        setVisible={_ => setShowNercExportDialog(false)}
        utilitySelectionList={utilitySelectionList}
        setSpinning={setSpinning}
        setError={setError}
        spinning={spinning}
        validYearRange={validYearRange}
      />
      <JcTmpErrorDialog
        errors={tmpError}
        visible={tmpError.length > 0}
        closeErrorPrompt={_ => setTmpError([])}
        title="Error/Warning"
      />

      <CriteriaRow>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
            width: '200px',
          }}
        >
          Wind Power Data
        </Title>
        <Select
          onChange={setSelUtility}
          css={{ minWidth: '400px' }}
          value={selUtility}
        >
          {utilityList.map(m => (
            <Select.Option key={m.key} value={m.key}>
              {m.key + '-' + m.value}
            </Select.Option>
          ))}
        </Select>

        <DatePicker.MonthPicker
          css={{ marginLeft: '10px' }}
          allowClear={false}
          // mode={['month']}
          // mode="decade"
          format="MMMM-YYYY"
          value={selectedDate.startOf('month')}
          // open={openPicker}
          onChange={dates => {
            setSelectedDate(dates)
          }}
          onPanelChange={dates => {
            setSelectedDate(dates)
          }}
        />
        <Button
          icon="database"
          loading={spinning}
          onClick={retrieve}
          css={{ marginLeft: '10px' }}
          type="primary"
        >
          Retrieve
        </Button>

        {hasWindPowerAdminPermisiion && (
          <Button
            css={{ marginLeft: '10px' }}
            onClick={onSave}
            icon="edit"
            type="primary"
          >
            Save
          </Button>
        )}
        <Filler />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          style={{ marginLeft: '10px', marginRight: '10px', maxWidth: '200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <Button
          css={{ marginLeft: '10px' }}
          onClick={_ => setShowNercExportDialog(true)}
        >
          Export NERC File
        </Button>
      </CriteriaRow>
      {hasWindPowerAdminPermisiion && (
        <CriteriaRow css={{ paddingTop: '0px' }}>
          <Filler />
          <Upload
            css={{ marginLeft: '10px' }}
            {...fileProps}
            action={url(importFileName)('performance')}
            // action={url('csv')}
            onChange={data => {
              setSpinning(true)
              onFileUploadChange(data)
            }}
            multiple={false}
            showUploadList={false}
            beforeUpload={file => setImportFileName(file.name)}
          >
            <Button>
              <Icon type="upload" />
              Performance
            </Button>
          </Upload>
          <Upload
            css={{ marginLeft: '10px' }}
            {...fileProps}
            action={url(importFileName)('outage')}
            // action={url('csv')}
            onChange={data => {
              setSpinning(true)
              onFileUploadChange(data)
            }}
            multiple={false}
            showUploadList={false}
            beforeUpload={file => setImportFileName(file.name)}
          >
            <Button>
              <Icon type="upload" /> Outage
            </Button>
          </Upload>
          <Button
            css={{ marginLeft: '10px' }}
            onClick={onValidation}
            disabled={selUtility === 'ALL'}
            type="danger"
          >
            Level 2 Validation
          </Button>
        </CriteriaRow>
      )}
      <FullHeightSizer heightOffset={250}>
        {({ height, width }) => (
          <Content css={{ height: height - 20, paddingTop: '10px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gridTemplateRows: '200px auto',
                height: height - 20,
                gridRowGap: '5px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                }}
              >
                <Grid
                  fuzzyFilter={fuzzyFilter}
                  sortEnabled={false}
                  data={data}
                  headers={PerformanceHeaders}
                  // altBy={d => d.isOld}
                  altBgColor="#ebebeb"
                  render={virtualizedGridRenderer({
                    autoFixColByKey: false,
                    rowHeaderRender,
                    height: 200,
                    width: width - 60,
                    headerRowHeight: 60,
                    cellRender: customizedCellRender,
                  })}
                  selectionMode="single"
                  selectionType="cell"
                  onSelectionChange={({ selectedRows }) =>
                    setSelectedPerformance(selectedRows)
                  }
                  onEditInfoChange={editInfo =>
                    setDataEditInfo(
                      editInfo,
                      setsubgroupEditInfo,
                      data,
                      setData
                    )
                  }
                  editInfo={subgroupEditInfo}
                  isEditable={({ header }) =>
                    !header.isKey && hasWindPowerAdminPermisiion
                  }
                  editMode="cell"
                  initialSortOptions={PerformanceHeaders}
                />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateRows: 'min-content ' + (height - 200) + 'px',
                }}
              >
                {hasWindPowerAdminPermisiion && (
                  <div
                    css={{
                      margin: '5px',
                      display: 'flex',
                      justifyContent: 'flexEnd',
                      columnGap: '10px',
                      borderTop: '1px solid #aaa',
                      padding: '2px',
                    }}
                  >
                    <Filler />
                    <div style={{ display: 'grid', alignContent: 'center' }}>
                      System
                    </div>
                    <Select
                      value={selectedComponenetName}
                      onChange={value => {
                        setSelectComponentName(value)
                        setSelectedComponentType('')
                      }}
                      style={{ width: '200px' }}
                    >
                      {Object.keys(componentMap).map(k => (
                        <Select.Option key={k} value={k}>
                          {k}
                        </Select.Option>
                      ))}
                    </Select>
                    <div style={{ display: 'grid', alignContent: 'center' }}>
                      Component
                    </div>
                    <Select
                      value={selectedComponenetType}
                      onChange={value => setSelectedComponentType(value)}
                      style={{ width: '200px' }}
                    >
                      {(componentMap[selectedComponenetName] || []).map(c => (
                        <Select.Option
                          key={c.windComponentId}
                          value={c.windComponentId}
                        >
                          {c.windComponent}
                        </Select.Option>
                      ))}
                    </Select>

                    <Button onClick={onAddOutage} type="primary">
                      Add Outage
                    </Button>
                    <Button onClick={onRemoveOutage} type="danger">
                      Remove Outage
                    </Button>
                  </div>
                )}
                <Grid
                  fuzzyFilter={fuzzyFilter}
                  sortEnabled={false}
                  data={outageData}
                  headers={OutageHeaders}
                  altBgColor="#ebebeb"
                  render={virtualizedGridRenderer({
                    autoFixColByKey: false,
                    rowHeaderRender,
                    height: height - 300,
                    width: width - 60,
                    headerRowHeight: 60,
                  })}
                  onSelectionChange={({ selectedRows }) =>
                    setSelectedOutageData(selectedRows)
                  }
                  onEditInfoChange={editInfo =>
                    setDataEditInfo(
                      editInfo,
                      setOutageEditInfo,
                      outageData,
                      setOutageData
                    )
                  }
                  editInfo={outageEditInfo}
                  isEditable={({ header }) =>
                    !header.isKey && hasWindPowerAdminPermisiion
                  }
                  editMode="cell"
                />
              </div>
            </div>
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  validYearRange: getValidYearRange(state),
})
export default connect(mapStateToProps)(WindPowerData)

// eslint-disable-next-line no-unused-vars
const WindSubgroupHeaders = [
  strCol({ ident: 'utility', display: 'Utility', isKey: true }),
  strCol({ ident: 'region', display: 'Region', isFiltered: true }),
  strCol({ ident: 'plantIdName', display: 'PlantIdName', isKey: true }),
  strCol({ ident: 'groupIdName', display: 'GroupIdName', isKey: true }),
  numCol({ ident: 'subgroupId', display: 'SubgroupId', isKey: true }),
  strCol({ ident: 'subgroupName', display: 'SubgroupName', isFiltered: true }),
  strCol({
    ident: 'isoResourceId',
    display: 'IsoResourceId',
    isFiltered: true,
  }),
  strCol({ ident: 'country', display: 'Country', isFiltered: true }),
  strCol({ ident: 'nearestCity', display: 'NearestCity', isFiltered: true }),
  strCol({
    ident: 'stateProvince',
    display: 'StateProvince',
    isFiltered: true,
  }),
  numCol({ ident: 'latitude', display: 'Latitude', isFiltered: true }),
  numCol({ ident: 'longitude', display: 'Longitude', isFiltered: true }),
  numCol({ ident: 'elevation', display: 'Elevation', isFiltered: true }),
  numCol({ ident: 'windRegime', display: 'WindRegime', isFiltered: true }),
  numCol({
    ident: 'annualAvgWindspeed',
    display: 'AnnualAvgWindspeed',
    isFiltered: true,
  }),
  strCol({
    ident: 'scadaManufacturer',
    display: 'ScadaManufacturer',
    isFiltered: true,
  }),
  strCol({ ident: 'scadaModel', display: 'ScadaModel', isFiltered: true }),
  numCol({
    ident: 'commissionYear',
    display: 'CommissionYear',
    isFiltered: true,
  }),
  numCol({
    ident: 'totalInstalledCap',
    display: 'TotalInstalledCap',
    isFiltered: true,
  }),
  numCol({ ident: 'reserveCap', display: 'ReserveCap', isFiltered: true }),
  numCol({
    ident: 'numberTurbines',
    display: 'NumberTurbines',
    isFiltered: true,
  }),
  numCol({
    ident: 'trubineSystemMw',
    display: 'TrubineSystemMw',
    isFiltered: true,
  }),
  numCol({
    ident: 'maxTurbineCapacity',
    display: 'MaxTurbineCapacity',
    isFiltered: true,
  }),
  strCol({
    ident: 'trubineManufacturer',
    display: 'TrubineManufacturer',
    isFiltered: true,
  }),
  strCol({ ident: 'trubineModel', display: 'TrubineModel', isFiltered: true }),
  strCol({
    ident: 'trubineModelVersion',
    display: 'TrubineModelVersion',
    isFiltered: true,
  }),
  numCol({ ident: 'rotorHeight', display: 'RotorHeight', isFiltered: true }),
  numCol({
    ident: 'rotorDiameter',
    display: 'RotorDiameter',
    isFiltered: true,
  }),
  numCol({
    ident: 'cutInWindspeed',
    display: 'CutInWindspeed',
    isFiltered: true,
  }),
  numCol({
    ident: 'lowCutOutWindspeed',
    display: 'LowCutOutWindspeed',
    isFiltered: true,
  }),
  numCol({
    ident: 'highCutOutWindspeed',
    display: 'HighCutOutWindspeed',
    isFiltered: true,
  }),
  numCol({
    ident: 'turbulenceIntensity',
    display: 'TurbulenceIntensity',
    isFiltered: true,
  }),
  numCol({ ident: 'avgWindspeed', display: 'AvgWindspeed', isFiltered: true }),
  numCol({ ident: 'windShearId', display: 'WindShearId', isFiltered: true }),
  numCol({
    ident: 'refAnemometerHeight',
    display: 'RefAnemometerHeight',
    isFiltered: true,
  }),
  numCol({
    ident: 'minOperatingTemp',
    display: 'MinOperatingTemp',
    isFiltered: true,
  }),
  numCol({
    ident: 'maxOperatingTemp',
    display: 'MaxOperatingTemp',
    isFiltered: true,
  }),
  strCol({
    ident: 'subgroupOwnershipStatus',
    display: 'SubgroupOwnershipStatus',
    isFiltered: true,
  }),
  dateCol({
    ident: 'effectiveDate',
    display: 'EffectiveDate',
    isFiltered: true,
  }),
  strCol({
    ident: 'transferToUtility',
    display: 'TransferToUtility',
    isFiltered: true,
  }),
]

// eslint-disable-next-line no-unused-vars
const WindOutageHeaders = (subgroupIdNameMap, componentIdNameMap) => [
  // strCol({ ident: 'utility', display: 'Utility', isKey: true }),
  // strCol({ ident: 'plantIdName', display: 'Plant', isKey: true }),
  // strCol({ ident: 'groupIdName', display: 'Group', isKey: true }),
  // numCol({ ident: 'subgroupId', display: 'Sub-Group', isKey: true }),
  numCol({
    ident: 'subgroupId',
    display: 'Sub-Group',
    isKey: true,
    ...toSelectionColProps(subgroupIdNameMap),
    width: 180,
  }),
  // intCol({ ident: 'year', display: 'Year', isKey: true, width: 50 }),
  // intCol({ ident: 'month', display: 'Month', isKey: true, width: 50 }),
  // numCol({ ident: 'componentId', display: 'Component', isKey: true }),
  // numCol({
  //   ident: 'componentId',
  //   display: 'Component',
  //   isKey: true,
  //   // ...toSelectionColProps(componentIdNameMap),
  //   width: 200,
  // }),
  strCol({
    ident: 'componentName',
    display: 'Component',
    isKey: true,
    width: 300,
  }),
  numCol({ ident: 'forcedThrs', display: 'Forced THours', isFiltered: true }),
  numCol({
    ident: 'numberForced',
    display: 'Number Of Forced',
    isFiltered: true,
  }),
  numCol({
    ident: 'maintenanceThrs',
    display: 'Maintenance THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'numberMaintenance',
    display: 'Number Of Maintenance',
    isFiltered: true,
  }),
  numCol({ ident: 'plannedThrs', display: 'Planned THours', isFiltered: true }),
  numCol({
    ident: 'numberPlanned',
    display: 'Number Of Planned',
    isFiltered: true,
  }),
  numCol({
    ident: 'eqForcedDthrs',
    display: 'Eq. Forced Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'eqMaintenanceDthrs',
    display: 'Eq. Maintenance Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'eqPlannedDthrs',
    display: 'Eq. Planned Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'forcedDelayThrs',
    display: 'Forced Delay THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'maintenanceDelayThrs',
    display: 'Maintenance Delay THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'plannedDelayThrs',
    display: 'Planned Derated THours',
    isFiltered: true,
  }),
]

const WindPerformanceHeaders = (subgroupIdNameMap, unitStatusType) => [
  // strCol({ ident: 'utility', display: 'Utility', isKey: true }),
  strCol({
    ident: 'plantIdName',
    display: 'Plant',
    isKey: true,
    width: 200,
    sortOrder: 'asc',
  }),
  strCol({
    ident: 'groupIdName',
    display: 'Group',
    isKey: true,
    width: 150,
    sortOrder: 'asc',
  }),
  // numCol({ ident: 'subgroupId', display: 'SubgroupId', isKey: true }),
  strCol({
    ident: 'subgroupName',
    display: 'Sub-Group',
    isKey: true,
    width: 180,
    // ...toSelectionColProps(subgroupIdNameMap),
  }),
  intCol({ ident: 'year', display: 'Year', isKey: true, width: 50 }),
  intCol({ ident: 'month', display: 'Month', isKey: true, width: 50 }),
  strCol({
    ident: 'status',
    display: 'Status',
    isFiltered: true,
    ...toSelectionColProps(unitStatusType),
    width: 150,
  }),
  numCol({ ident: 'grossactgen', display: 'Gross Gen MWh', isFiltered: true }),
  numCol({ ident: 'netactgen', display: 'Net Gen MWh', isFiltered: true }),
  numCol({ ident: 'netmaxcap', display: 'Net Max Cap', isFiltered: true }),
  numCol({ ident: 'activeThrs', display: 'Active THours', isFiltered: true }),
  numCol({
    ident: 'contactorThrs',
    display: 'Contact Thours',
    isFiltered: true,
  }),
  numCol({
    ident: 'rsThrs',
    display: 'Reserve Shutdown  THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'ruThrs',
    display: 'Reserve Unavailable THours',
    isFiltered: true,
  }),
  numCol({ ident: 'forcedThrs', display: 'Forced THours', isFiltered: true }),
  numCol({
    ident: 'omcForcedThrs',
    display: 'Omc Forced THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'maintenanceThrs',
    display: 'Maintenance THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'omcMaintenanceThrs',
    display: 'Omc Maintenance THours',
    isFiltered: true,
  }),
  numCol({ ident: 'plannedThrs', display: 'Planned THours', isFiltered: true }),
  numCol({
    ident: 'omcPlannedThrs',
    display: 'Omc Planned THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'inactiveReserveThrs',
    display: 'Inactive Reserve THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'mothballedThrs',
    display: 'Mothballed THours',
    isFiltered: true,
  }),
  numCol({ ident: 'retiredThrs', display: 'Retired THours', isFiltered: true }),
  numCol({
    ident: 'eqForcedDthrs',
    display: 'Eq. Forced Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'omcEqForcedDthrs',
    display: 'Omc Eq. Forced Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'eqMaintenanceDthrs',
    display: 'Eq. Maintenance Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'omcEqMaintenanceDthrs',
    display: 'Omc Eq. Maintenance Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'eqPlannedDthrs',
    display: 'Eq. Planned Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'omcEqPlannedDthrs',
    display: 'Omc Eq. Planned Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'eqRsDthrs',
    display: 'Eq. RS Derate THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'forcedDelayThrs',
    display: 'Forced Delay THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'maintenanceDelayThrs',
    display: 'Maintenance Delay THours',
    isFiltered: true,
  }),
  numCol({
    ident: 'plannedDelayThrs',
    display: 'Planned Delay THours',
    isFiltered: true,
  }),
  // strCol({ ident: 'validated', display: 'Validated', isKey: true }),
  modifierCol,
  modifiedDateCol,
]
